import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    FormFeedback,
    Spinner
} from 'reactstrap';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import moment from 'moment';
import { VenderMasterDetailsGetData } from 'pages/Masters/Vendor-Master/VenderControler';

const DateRangeModal = ({ isOpen, toggle, onApply, supplier, validFromDate, validToDate }) => {
    // State variables
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [numDays, setNumDays] = useState(0);
    const [errors, setErrors] = useState({});
    const [isSupplierHaveDates, setIsSupplierHaveDates] = useState(false);
    const [loading, setLoading] = useState(false);
    const [datesChangedByUser, setDatesChangedByUser] = useState(false);
    // Update number of days when dates change
    useEffect(() => {
        // Skip this effect during supplier data loading to prevent overriding supplier's order cycle
        if (!loading && !isSupplierHaveDates && fromDate && toDate && datesChangedByUser) {
            // Calculate the difference in days
            const diffTime = Math.abs(new Date(toDate) - new Date(fromDate));
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays >= 0) {
                setNumDays(diffDays);
            }
        }
    }, [fromDate, toDate, isSupplierHaveDates, loading, datesChangedByUser]);    // Reset the form when modal opens
    useEffect(() => {
        if (isOpen) {
            resetForm();

            const fetchSupplierData = async () => {
                if (supplier) {
                    setLoading(true);
                    try {
                        const response = await VenderMasterDetailsGetData(supplier);
                        if (response && response.success) {
                            if (response.data && response.data.intOrderCycle > 0) {
                                const orderCycle = response.data.intOrderCycle;
                                const fromDate = new Date();
                                const toDate = new Date();
                                toDate.setDate(fromDate.getDate() + orderCycle);
                                setFromDate(fromDate);
                                setToDate(toDate);
                                setNumDays(orderCycle);
                                setIsSupplierHaveDates(true);
                            } else {
                                setIsSupplierHaveDates(false);
                            }
                        } else {
                            setIsSupplierHaveDates(false);
                        }
                    } catch (error) {
                        console.error("Error fetching supplier details:", error);
                        setIsSupplierHaveDates(false);
                    } finally {
                        setLoading(false);
                    }
                }
            };

            fetchSupplierData();
        }
    }, [isOpen, supplier]);    // Reset form to default values
    const resetForm = () => {
        setFromDate(validFromDate != "" ? validFromDate : new Date());
        setToDate(validToDate != "" ? validToDate : new Date());
        setNumDays(0);
        setErrors({});
        setDatesChangedByUser(false);
        setIsSupplierHaveDates(false);
    };

    // Validate form before submitting
    const validateForm = () => {
        const newErrors = {};

        if (!fromDate) {
            newErrors.fromDate = 'From date is required';
        }

        if (!toDate) {
            newErrors.toDate = 'To date is required';
        }

        if (toDate < fromDate) {
            newErrors.toDate = 'To date must be after from date';
        }

        if (!numDays || isNaN(numDays) || parseInt(numDays) < 0) {
            newErrors.numDays = 'Enter a valid number of days';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleApply = () => {
        if (validateForm()) {
            onApply({
                fromDate: moment(fromDate).format("YYYY-MM-DD"),
                toDate: moment(toDate).format("YYYY-MM-DD"),
                numDays: parseInt(numDays)
            });
            toggle();
        }
    };    // Handle number of days input change
    const handleDaysChange = (e) => {
        if (isSupplierHaveDates) return; // Don't change if supplier has predefined dates

        const value = parseInt(e.target.value) || 0;
        setNumDays(value);

        // Update the toDate based on the number of days input
        if (fromDate && value >= 0) {
            const newToDate = new Date(fromDate);
            newToDate.setDate(newToDate.getDate() + value);
            setToDate(newToDate);
            setDatesChangedByUser(true);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered backdrop="static">
            <ModalHeader toggle={toggle}>Auto Order Selection</ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center p-4">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading supplier cycle details...</p>
                    </div>
                ) : (
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="fromDate">From Date</Label>
                                    <Flatpickr
                                        className={`form-control ${errors.fromDate ? 'is-invalid' : ''}`}
                                        value={fromDate}
                                        onChange={date => {
                                            setFromDate(date[0]);
                                            setDatesChangedByUser(true);
                                        }}
                                        options={{
                                            dateFormat: "Y-m-d",
                                            allowInput: true,
                                            disabled: isSupplierHaveDates
                                        }}
                                    />
                                    {errors.fromDate && <FormFeedback>{errors.fromDate}</FormFeedback>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="toDate">To Date</Label>
                                    <Flatpickr
                                        className={`form-control ${errors.toDate ? 'is-invalid' : ''}`}
                                        value={toDate}
                                        onChange={date => {
                                            setToDate(date[0]);
                                            setDatesChangedByUser(true);
                                        }}
                                        options={{
                                            dateFormat: "Y-m-d",
                                            allowInput: true,
                                            minDate: fromDate,
                                            disabled: isSupplierHaveDates
                                        }}
                                    />
                                    {errors.toDate && <FormFeedback>{errors.toDate}</FormFeedback>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className='mt-2 '>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="numDays">Number of order cycle days</Label>
                                    <Input
                                        type="number"
                                        id="numDays"
                                        name="numDays"
                                        value={numDays}
                                        onChange={handleDaysChange}
                                        min="0"
                                        className={errors.numDays ? 'is-invalid' : ''}
                                        disabled={isSupplierHaveDates}
                                    />
                                    {errors.numDays && <FormFeedback>{errors.numDays}</FormFeedback>}
                                    {isSupplierHaveDates && (
                                        <small className="text-info mt-1 d-block">
                                            Using supplier's predefined order cycle ({numDays} days)
                                        </small>
                                    )}
                                    {!isSupplierHaveDates && supplier && (
                                        <small className="text-muted mt-1 d-block">
                                            Selected supplier does not have a predefined order cycle
                                        </small>
                                    )}
                                </FormGroup>
                            </Col></Row>
                    </Form>
                )}
            </ModalBody>            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button color="primary" onClick={handleApply} disabled={loading}>Ok</Button>
            </ModalFooter>
        </Modal>
    );
};

export default DateRangeModal;
